import { createFileRoute } from "@tanstack/react-router";
import * as React from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { DirectionButton } from "@/components/DirectionButton";
import { SquadButton } from "@/components/SquadButton";
import { DurationButton } from "@/components/DurationButton";
import { PrimaryButton } from "@/components/PrimaryButton";
import { SecondaryButton } from "@/components/SecondaryButton";
import { ErrorButton } from "@/components/ErrorButton";
import { BlueButton } from "@/components/BlueButton";
import { DefaultButton } from "@/components/DefaultButton";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import FootballField from "@/assets/field.svg?react";
import { AddTokenContainer } from "@/components/AddTokenContainer";
import { DatePickerCalendar } from "@/components/DatePickerCalendar";
import { DirectionToggle } from "@/components/DirectionToggle";
import { EntriesCounter } from "@/components/EntriesCounter";
import { WavyText } from "@/components/WavyText";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

export const Route = createFileRoute("/ui-showcase")({
  component: Testing,
});

function Testing() {
  const [count, setCount] = React.useState(0);
  const [textTransform, setTextTransform] = React.useState<
    "uppercase" | "lowercase" | "capitalize"
  >("uppercase");
  const [selectedFont, setSelectedFont] = React.useState("m6x11");
  const [direction, setDirection] = React.useState<"long" | "short">("long");
  const [entries, setEntries] = React.useState(0);

  const availableFonts = ["DeadRevolver", "DeadRevolverDisplay", "m6x11"];

  const squads = React.useMemo(
    () => [
      { name: "Alpha Squad", pnl: +12 },
      { name: "Beta Squad", pnl: -34 },
      { name: "Gamma Squad", pnl: +256 },
      { name: "Delta Squad", pnl: -5 },
    ],
    []
  );
  const [selectedSquadIndex, setSelectedSquadIndex] = React.useState(0);
  const [hoveredSquadIndex, setHoveredSquadIndex] = React.useState<
    number | null
  >(null);

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-5xl mx-auto py-8 px-4">
        <h1 className="text-2xl font-bold mb-6">Custom Components Showcase</h1>

        {/* Demo row: spans full width (3x1) and centered */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="space-y-4 md:col-span-3">
            <h2 className="text-lg font-semibold">Demo</h2>
            <div className="max-w-xs flex flex-col gap-4 items-center">
              <FootballField className="w-full" />
              <div className="w-full flex flex-row items-center justify-center gap-2">
                <DirectionButton direction="long" />
                <DurationButton />
                <DirectionButton direction="short" />
              </div>
              <div className="w-full flex flex-row items-center gap-3">
                <Carousel className="w-full">
                  <CarouselContent>
                    {squads.map((squad, idx) => {
                      const isSelected = idx === selectedSquadIndex;
                      const isHovered = idx === hoveredSquadIndex;
                      const dimmed = !isSelected && !isHovered;
                      return (
                        <CarouselItem key={squad.name} className="basis-auto">
                          <div
                            onClick={() => setSelectedSquadIndex(idx)}
                            onMouseEnter={() => setHoveredSquadIndex(idx)}
                            onMouseLeave={() => setHoveredSquadIndex(null)}
                            className={cn(
                              "transition",
                              dimmed ? "grayscale" : ""
                            )}
                          >
                            <SquadButton
                              squadName={squad.name}
                              pnl={squad.pnl}
                              muted={dimmed}
                            />
                          </div>
                        </CarouselItem>
                      );
                    })}
                    {/* Add Squad as the last item (never grayscaled) */}
                    <CarouselItem className="basis-auto">
                      <Button
                        variant="add-squad"
                        size="pixel"
                        aria-label="Add Squad"
                      />
                    </CarouselItem>
                  </CarouselContent>
                </Carousel>
              </div>
            </div>
          </div>
          <div className="space-y-4 md:col-span-3">
            <h2 className="text-lg font-semibold">Wavy Text</h2>
            <WavyText
              text="Lorem Ipsum is simply dummy text of the printing and typesetting industry."
              className="text-gray-700 leading-relaxed"
            />
          </div>
        </div>

        {/* Grid 3x3 layout of remaining sections */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Font Showcase */}
          <div
            className="space-y-4 md:col-span-3"
            style={{ fontFamily: selectedFont }}
          >
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">
                Font Showcase - {selectedFont}
              </h2>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant={
                    textTransform === "uppercase" ? "default" : "outline"
                  }
                  onClick={() => setTextTransform("uppercase")}
                  className="text-xs"
                >
                  UPPERCASE
                </Button>
                <Button
                  size="sm"
                  variant={
                    textTransform === "lowercase" ? "default" : "outline"
                  }
                  onClick={() => setTextTransform("lowercase")}
                  className="text-xs"
                >
                  lowercase
                </Button>
                <Button
                  size="sm"
                  variant={
                    textTransform === "capitalize" ? "default" : "outline"
                  }
                  onClick={() => setTextTransform("capitalize")}
                  className="text-xs"
                >
                  Capitalize
                </Button>
              </div>
            </div>

            {/* Font Selector */}
            <div className="flex items-center gap-3">
              <label className="text-sm font-semibold text-gray-700">
                Select Font:
              </label>
              <Select value={selectedFont} onValueChange={setSelectedFont}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Select a font" />
                </SelectTrigger>
                <SelectContent>
                  {availableFonts.map((font) => (
                    <SelectItem key={font} value={font}>
                      {font}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Font Sizes */}
              <div className={cn("space-y-2", textTransform)}>
                <h4 className="font-semibold text-sm text-gray-700">
                  Font Sizes
                </h4>
                <div className="text-xs text-gray-800">text-xs</div>
                <div className="text-sm text-gray-800">text-sm</div>
                <div className="text-base text-gray-800">text-base</div>
                <div className="text-lg text-gray-800">text-lg</div>
                <div className="text-xl text-gray-800">text-xl</div>
                <div className="text-2xl text-gray-800">text-2xl</div>
              </div>

              {/* Font Weights */}
              <div className={cn("space-y-2", textTransform)}>
                <h4 className="font-semibold text-sm text-gray-700">
                  Font Weights & Styles
                </h4>
                <div className="text-base font-normal text-gray-800">
                  normal
                </div>
                <div className="text-base font-bold text-gray-800">bold</div>
                <div className="text-base italic text-gray-800">italic</div>
                <div className="text-base font-bold italic text-gray-800">
                  bold italic
                </div>
              </div>

              {/* Letter Spacing */}
              <div className={cn("space-y-2", textTransform)}>
                <h4 className="font-semibold text-sm text-gray-700">
                  Letter Spacing
                </h4>
                <div className="text-base tracking-tight text-gray-800">
                  tight
                </div>
                <div className="text-base tracking-normal text-gray-800">
                  normal
                </div>
                <div className="text-base tracking-wide text-gray-800">
                  wide
                </div>
                <div className="text-base tracking-wider text-gray-800">
                  wider
                </div>
              </div>
            </div>

            {/* Full pangram */}
            <div className={cn("mt-4 p-4 bg-gray-50 rounded", textTransform)}>
              <p className="text-lg">
                He moved quickly, stacking supplies in order. Pack my box with
                five dozen liquor jugs—the last line on his list. It wasn’t
                greed, just preparation. The jugs clinked softly, echoing
                through the empty room as the storm rolled in.
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <h2 className="text-lg font-semibold">Framed Buttons</h2>
            <div className="flex flex-wrap gap-3">
              {/* Framed versions */}
              <PrimaryButton>Primary (framed)</PrimaryButton>
              <SecondaryButton>Secondary (framed)</SecondaryButton>
              <ErrorButton>Destructive (framed)</ErrorButton>
              <BlueButton>Blue (framed)</BlueButton>
              <DefaultButton>Default (tablist)</DefaultButton>
            </div>
          </div>

          {/* Framed Buttons (exclude long/short to avoid duplication) */}
          <div className="flex flex-col gap-4">
            {/* Icon Increment/Decrement */}
            <div className="space-y-4">
              <h2 className="text-lg font-semibold">
                Icon Increment/Decrement
              </h2>
              <div className="flex items-center gap-4">
                <Button
                  variant="custom-ui-decrement"
                  aria-label="Decrement"
                  onClick={() => setCount((c) => c - 1)}
                />
                <span className="min-w-12 text-center font-mono text-lg">
                  {count}
                </span>
                <Button
                  variant="custom-ui-increment"
                  aria-label="Increment"
                  onClick={() => setCount((c) => c + 1)}
                />
              </div>
            </div>

            {/* Disabled States */}
            <div className="space-y-4">
              <h2 className="text-lg font-semibold">Disabled States</h2>
              <div className="flex flex-wrap items-center gap-3">
                <PrimaryButton disabled>Primary (disabled)</PrimaryButton>
                <Button
                  variant="custom-ui-decrement"
                  aria-label="Decrement"
                  disabled
                />
                <Button
                  variant="custom-ui-increment"
                  aria-label="Increment"
                  disabled
                />
              </div>
            </div>

            {/* Containers */}
            <div className="space-y-4">
              <div className="space-y-4">
                <h2 className="text-lg font-semibold">Add Token Container</h2>
                <div className="flex flex-wrap items-center gap-4">
                  <AddTokenContainer className="size-24">
                    <div className="flex flex-col items-center justify-center h-full text-center">
                      <span>Add token</span>
                    </div>
                  </AddTokenContainer>
                  <AddTokenContainer className="size-24" value={{ power: 96 }}>
                    testing
                  </AddTokenContainer>
                </div>
              </div>
            </div>
          </div>

          {/* Custom Input */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold">Custom Input</h2>
            <div className="grid gap-4 max-w-md">
              <div className="space-y-2">
                <label className="text-sm font-medium">Default</label>
                <Input placeholder="Type here" />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">With Value</label>
                <Input defaultValue="Hello CFL" />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Disabled</label>
                <Input placeholder="Disabled" disabled />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Invalid</label>
                <Input placeholder="Invalid state" aria-invalid="true" />
              </div>
            </div>
          </div>

          {/* Tabs Demo */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold">Tabs</h2>
            <Tabs defaultValue="overview" className="w-full">
              <TabsList>
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="buttons">Buttons</TabsTrigger>
                <TabsTrigger value="inputs">Inputs</TabsTrigger>
              </TabsList>
              <div className="mt-3 rounded-lg border border-gray-200 bg-white p-4">
                <TabsContent value="overview">
                  <p className="text-sm text-gray-700">
                    This tab system is wired via our shadcn-style Tabs component
                    (Radix Tabs under-the-hood) with Tailwind styling.
                  </p>
                </TabsContent>
                <TabsContent value="buttons">
                  <div className="flex flex-wrap items-center gap-2">
                    <Button variant="default">Primary</Button>
                    <DurationButton />
                    <DirectionButton direction="long" />
                    <DirectionButton direction="short" />
                  </div>
                </TabsContent>
                <TabsContent value="inputs">
                  <div className="grid gap-2 max-w-sm">
                    <Input placeholder="Type here" />
                    <Input placeholder="Disabled" disabled />
                  </div>
                </TabsContent>
              </div>
            </Tabs>
          </div>

          <div className="space-y-4">
            {/* Date Picker Demo */}
            <div className="space-y-4">
              <h2 className="text-lg font-semibold">
                Date Picker (Calendar in Popover)
              </h2>
              <div className="space-y-3">
                <DatePickerCalendar />
              </div>
            </div>
            {/* Direction Toggle Demo */}
            <div className="space-y-4">
              <h2 className="text-lg font-semibold">Direction Toggle</h2>
              <div className="space-y-4">
                <DirectionToggle value={direction} onChange={setDirection} />
                <p className="text-sm text-gray-600">
                  Selected:{" "}
                  <span className="font-bold">{direction.toUpperCase()}</span>
                </p>
              </div>
            </div>
          </div>
          {/* Entries Counter Demo */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold">Entries Counter</h2>
            <div className="space-y-4">
              <div className="flex flex-col space-y-2">
                <EntriesCounter
                  value={entries}
                  onChange={setEntries}
                  direction={direction}
                />
                <p className="text-sm text-gray-600">
                  Current entries: <span className="font-bold">{entries}</span>
                  {" | "}
                  Background matches:{" "}
                  <span className="font-bold">{direction.toUpperCase()}</span>
                </p>
              </div>
            </div>
          </div>

          {/* Sonner Toast Demo */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold">Sonner Toasts</h2>
            <p className="text-sm text-gray-600">
              Custom styled toasts with green-container border for success toasts
            </p>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => toast("Default toast message")}
                >
                  Default Toast
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => toast.success("Success message!")}
                >
                  Success Toast
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => toast.error("Error message!")}
                >
                  Error Toast
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => toast.warning("Warning message!")}
                >
                  Warning Toast
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => toast.info("Info message!")}
                >
                  Info Toast
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => toast.loading("Loading...")}
                >
                  Loading Toast
                </Button>
              </div>
              <div className="space-y-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => toast("Toast with action", {
                    action: {
                      label: "Undo",
                      onClick: () => toast("Undo clicked!")
                    }
                  })}
                >
                  Toast with Action
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => toast.promise(
                    new Promise((resolve) => setTimeout(resolve, 2000)),
                    {
                      loading: "Saving...",
                      success: "Saved successfully!",
                      error: "Failed to save."
                    }
                  )}
                >
                  Promise Toast
                </Button>
              </div>
              
              {/* Success Toast Info */}
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-gray-700">Success Toast Styling</h4>
                <div className="text-xs text-gray-600">
                  <p>• Uses <code className="bg-gray-100 px-1 rounded">.green-container</code> border-image</p>
                  <p>• Transparent background with white text</p>
                  <p>• Green container border from Cloudinary image</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Toaster />
    </div>
  );
}
